import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import i18n from '@/lib/i18n'
import {
	enablePush,
	getPushPermission,
	listenForegroundPush,
	syncPushToken,
} from '@/lib/push'

/**
 * Mantém o push funcionando para o usuário logado: registra o token (se já permitido)
 * e, com a aba aberta, atualiza o sino e avisa por toast. O fetch normal continua como fallback.
 */
export function usePushListener() {
	const queryClient = useQueryClient()

	useEffect(() => {
		if (getPushPermission() !== 'granted') return

		syncPushToken().catch((error) =>
			console.error('[push] falha ao registrar o token do dispositivo', error),
		)

		let unsubscribe: (() => void) | undefined
		let cancelled = false
		listenForegroundPush((payload) => {
			// A rota do payload é a mesma do clique no push; o "kind" diz o que atualizar
			if (payload.data?.kind === 'NOTICE') {
				queryClient.invalidateQueries({ queryKey: ['notices'] })
			} else {
				queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] })
			}
			const { title, body } = payload.data ?? {}
			if (title) toast.info(body ? `${title}: ${body}` : title)
		})
			.then((off) => {
				if (cancelled) off()
				else unsubscribe = off
			})
			.catch((error) =>
				console.error('[push] falha ao escutar mensagens', error),
			)

		return () => {
			cancelled = true
			unsubscribe?.()
		}
	}, [queryClient])
}

export function usePushPermission() {
	const [permission, setPermission] = useState(getPushPermission)

	const [isPending, setIsPending] = useState(false)

	const run = useCallback(async (action: () => Promise<unknown>) => {
		setIsPending(true)
		try {
			await action()
			toast.success(i18n.t('push.success'))
		} catch (error) {
			console.error('[push] falha ao ativar as notificações', error)
			toast.error(i18n.t('push.error'))
		} finally {
			setPermission(getPushPermission())
			setIsPending(false)
		}
	}, [])

	/** Pede a permissão (se ainda não concedida) e registra o token do dispositivo. */
	const enable = useCallback(() => run(enablePush), [run])
	/** Registra de novo o token, com a permissão já concedida. */
	const resync = useCallback(() => run(syncPushToken), [run])

	return { permission, enable, resync, isPending }
}
